import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { Settings, Save } from 'lucide-react'
import { adminApi } from '@/lib/api'
import { useOrgContext } from '@/contexts/OrgContext'
import { OrgSetting } from '@/types/organization'

/**
 * The `auth.methods` vocabulary is owned by the login surfaces that read it
 * (client-app's `resolveLoginOptions`, and packages/app's AuthSettingsPanel).
 * This list used to read ['email', 'google', 'apple', 'saml'], which shares
 * only 'google' and 'apple' with that vocabulary: saving it wrote 'email' (not
 * understood, so email/password sign-in switched OFF) and 'saml' (ignored),
 * while offering no way to enable Microsoft. Keep this in step with
 * packages/app/src/features/organizations/components/AuthSettingsPanel.tsx.
 */
const AUTH_METHODS: { id: string; label: string; hint?: string }[] = [
  { id: 'google', label: 'Google' },
  { id: 'apple', label: 'Apple' },
  { id: 'microsoft', label: 'Microsoft' },
  { id: 'email_password', label: 'Email / Password' },
  { id: 'firebase', label: 'Firebase (default)', hint: 'Google plus email/password' },
]

export function AdminOrgSettingsPage() {
  const { selectedOrg, hasOrgContext, orgName: currentOrgName, orgSlug } = useOrgContext()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)

  // General
  const [orgName, setOrgName] = useState('')
  const [logoUrl, setLogoUrl] = useState('')

  // Auth
  const [authMethods, setAuthMethods] = useState<string[]>(['email', 'google'])
  const [jitProvisioning, setJitProvisioning] = useState('invite_only')
  const [externalCollaborators, setExternalCollaborators] = useState(false)
  // Cross-tenant directory opt-in ('self' = listed, otherwise opt-out)
  const [directoryListing, setDirectoryListing] = useState(false)

  // Publishing
  const [allowExternalSharing, setAllowExternalSharing] = useState(false)

  const loadSettings = async () => {
    // See MembersPage: a dedicated tenant has an implicit org and no selector.
    if (!hasOrgContext) return
    try {
      setLoading(true)
      const data = await adminApi.getOrgSettings()
      const parsed: OrgSetting[] = data.settings || []

      // Populate form state from settings
      setOrgName(currentOrgName ?? '')

      const logo = parsed.find(s => s.key === 'org.logo')
      setLogoUrl(typeof logo?.value === 'string' ? logo.value : '')

      const methods = parsed.find(s => s.key === 'auth.methods')
      if (methods?.value) {
        setAuthMethods(Array.isArray(methods.value) ? methods.value : ['email', 'google'])
      }

      const jit = parsed.find(s => s.key === 'auth.jit_provisioning')
      if (jit?.value) setJitProvisioning(jit.value)

      const extCollab = parsed.find(s => s.key === 'auth.external_collaborators')
      if (extCollab?.value !== null && extCollab?.value !== undefined) {
        setExternalCollaborators(!!extCollab.value)
      }

      const dirListing = parsed.find(s => s.key === 'auth.directory_listing')
      setDirectoryListing(dirListing?.value === 'self')

      const extSharing = parsed.find(s => s.key === 'publishing.allow_external_sharing')
      if (extSharing?.value !== null && extSharing?.value !== undefined) {
        setAllowExternalSharing(!!extSharing.value)
      }
    } catch (err) {
      console.error('Failed to load org settings:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadSettings() }, [selectedOrg?.slug, hasOrgContext])

  const saveSetting = async (key: string, value: any, category: string) => {
    setSaving(key)
    try {
      await adminApi.updateOrgSetting(key, value, category)
      await loadSettings()
    } catch (err) {
      console.error(`Failed to save ${key}:`, err)
    } finally {
      setSaving(null)
    }
  }

  const saveGeneral = async () => {
    if (!selectedOrg || !orgName) return
    setSaving('general')
    try {
      await adminApi.updateOrganization(selectedOrg.slug, { name: orgName })
    } catch (err) {
      console.error('Failed to save org name:', err)
    } finally {
      setSaving(null)
    }
  }

  if (!hasOrgContext) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Select an organization first from the Organizations page or the header selector.</p>
      </div>
    )
  }

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading settings...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Settings className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Organization Settings</h1>
          <p className="text-sm text-muted-foreground">{currentOrgName} ({orgSlug})</p>
        </div>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="authentication">Authentication</TabsTrigger>
          <TabsTrigger value="publishing">Publishing</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>General</CardTitle>
              <CardDescription>Basic organization information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  disabled={!selectedOrg}
                />
                {!selectedOrg && (
                  <p className="text-xs text-muted-foreground">
                    Managed by Ponder. Contact support to change your organization name.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Brand Mark URL</Label>
                <Input
                  value={logoUrl}
                  onChange={e => setLogoUrl(e.target.value)}
                  placeholder="https://…/logo.png"
                />
                <p className="text-xs text-muted-foreground">
                  Publicly reachable image, shown on the portal home and sign-in pages.
                  It must load without authentication, so it cannot be a file artifact.
                </p>
                {logoUrl && (
                  <div className="flex items-center gap-3 pt-1">
                    {/* Previewed on the same dark plate the portal uses. Client
                        marks are often white knockouts, and on a light card an
                        admin would see nothing and assume the URL was wrong. */}
                    <div className="rounded-xl bg-neutral-900 px-4 py-3">
                      <img
                        src={logoUrl}
                        alt="Brand mark preview"
                        className="h-7 w-auto object-contain"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">Preview</span>
                  </div>
                )}
                <Button
                  onClick={() => saveSetting('org.logo', logoUrl, 'general')}
                  disabled={saving === 'org.logo'}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving === 'org.logo' ? 'Saving...' : 'Save Brand Mark'}
                </Button>
              </div>
              {selectedOrg && (
                <div className="space-y-2">
                  <Label>Plan</Label>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline">{selectedOrg.plan || 'free'}</Badge>
                    <span className="text-xs text-muted-foreground">(Change via Organizations page)</span>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input value={orgSlug ?? ''} disabled />
              </div>
              {selectedOrg && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Badge className={selectedOrg.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}>
                    {selectedOrg.status}
                  </Badge>
                </div>
              )}
              {selectedOrg && (
                <Button onClick={saveGeneral} disabled={saving === 'general'}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving === 'general' ? 'Saving...' : 'Save'}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="authentication" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Authentication Methods</CardTitle>
              <CardDescription>Configure how users can sign in to this organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {AUTH_METHODS.map(method => (
                  <div key={method.id} className="flex items-center justify-between">
                    <div>
                      <Label>{method.label}</Label>
                      {method.hint && (
                        <p className="text-xs text-muted-foreground">{method.hint}</p>
                      )}
                    </div>
                    <Switch
                      checked={authMethods.includes(method.id)}
                      onCheckedChange={checked => {
                        setAuthMethods(prev =>
                          checked ? [...prev, method.id] : prev.filter(m => m !== method.id)
                        )
                      }}
                    />
                  </div>
                ))}
              </div>
              <Button
                onClick={() => saveSetting('auth.methods', authMethods, 'authentication')}
                disabled={saving === 'auth.methods'}
              >
                <Save className="mr-2 h-4 w-4" />
                {saving === 'auth.methods' ? 'Saving...' : 'Save Auth Methods'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User Provisioning</CardTitle>
              <CardDescription>Control how new users join this organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>JIT Provisioning Mode</Label>
                <Select value={jitProvisioning} onValueChange={setJitProvisioning}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open (anyone can join)</SelectItem>
                    <SelectItem value="admin_approval">Admin Approval</SelectItem>
                    <SelectItem value="invite_only">Invite Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>External Collaborators</Label>
                  <p className="text-xs text-muted-foreground">Allow users outside the org domain to be invited</p>
                </div>
                <Switch checked={externalCollaborators} onCheckedChange={setExternalCollaborators} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Cross-Org Directory Listing</Label>
                  <p className="text-xs text-muted-foreground">List this org's memberships in the cross-tenant identity directory so members see it in their org switcher. Off = the org contributes nothing (opt-out, the default).</p>
                </div>
                <Switch checked={directoryListing} onCheckedChange={setDirectoryListing} />
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => saveSetting('auth.jit_provisioning', jitProvisioning, 'authentication')}
                  disabled={saving === 'auth.jit_provisioning'}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving === 'auth.jit_provisioning' ? 'Saving...' : 'Save Provisioning'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => saveSetting('auth.external_collaborators', externalCollaborators, 'authentication')}
                  disabled={saving === 'auth.external_collaborators'}
                >
                  {saving === 'auth.external_collaborators' ? 'Saving...' : 'Save Collaborators'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => saveSetting('auth.directory_listing', directoryListing ? 'self' : 'none', 'authentication')}
                  disabled={saving === 'auth.directory_listing'}
                >
                  {saving === 'auth.directory_listing' ? 'Saving...' : 'Save Directory Listing'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="publishing" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Publishing</CardTitle>
              <CardDescription>Control how content is shared from this organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow External Sharing</Label>
                  <p className="text-xs text-muted-foreground">Let users share artifacts and publications outside the org</p>
                </div>
                <Switch checked={allowExternalSharing} onCheckedChange={setAllowExternalSharing} />
              </div>
              <Button
                onClick={() => saveSetting('publishing.allow_external_sharing', allowExternalSharing, 'publishing')}
                disabled={saving === 'publishing.allow_external_sharing'}
              >
                <Save className="mr-2 h-4 w-4" />
                {saving === 'publishing.allow_external_sharing' ? 'Saving...' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
